import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 60_000;

/**
 * Poll `/public/church/{code}/live/status` once a minute and return
 * the current YouTube live state. Safe to call from both public
 * (`PublicLive`, `Landing`) and authenticated (`Dashboard`) surfaces
 * — this endpoint is unauthenticated by design so first-time
 * visitors see the pulsing "LIVE NOW" badge too.
 *
 * The backend already caches the YouTube probe for 45s, so even with
 * hundreds of concurrent viewers polling at 60s intervals we hit
 * YouTube at most ~1x/minute.
 */
export function useLiveStatus(churchCode) {
  const [status, setStatus] = useState({ is_live: false, video_id: null, title: null });
  const timerRef = useRef(null);

  useEffect(() => {
    if (!churchCode) return undefined;
    let cancelled = false;

    const tick = async () => {
      try {
        const { data } = await axios.get(
          `${API}/public/church/${churchCode}/live/status`,
          { timeout: 10_000 }
        );
        if (!cancelled) setStatus(data || { is_live: false });
      } catch {
        // Keep the previous state on transient errors — avoids
        // flickering the badge off when a single poll fails.
      }
    };

    tick();
    timerRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [churchCode]);

  return status;
}
